import { getCurrentLocale } from '@shared/i18n'

const getValidDate = (dateValue: string | null | undefined): Date | null => {
	if (!dateValue) {
		return null
	}

	const date = new Date(dateValue)
	return Number.isNaN(date.getTime()) ? null : date
}

const addDays = (date: Date, daysAmount: number): Date => {
	const nextDate = new Date(date)
	nextDate.setDate(nextDate.getDate() + daysAmount)
	return nextDate
}

const getMonthLabel = (date: Date) => {
	const month = date
		.toLocaleString(getCurrentLocale(), { month: 'short' })
		.replace('.', '')

	return month.charAt(0).toUpperCase() + month.slice(1)
}

export const getProgramDayDate = ({
	calendarDate,
	dayNum,
	dueAt,
	weekStartAt,
}: {
	calendarDate?: string | null
	dayNum: number
	dueAt?: string | null
	weekStartAt?: string | null
}): Date | null => {
	const calendarDayDate = getValidDate(calendarDate)
	if (calendarDayDate) {
		return calendarDayDate
	}

	const weekStartDate = getValidDate(weekStartAt)
	if (weekStartDate) {
		return addDays(weekStartDate, Math.max(dayNum - 1, 0))
	}

	const dueDate = getValidDate(dueAt)
	if (dueDate) {
		return dueDate
	}

	return null
}

export const getProgramDayDateLabel = (date: Date | null): string => {
	if (!date) {
		return ''
	}

	return `${date.getDate()} ${getMonthLabel(date)}`
}

export const getProgramDayDateParts = (
	date: Date | null
): { day: string; month: string } => {
	if (!date) {
		return { day: '', month: '' }
	}

	return {
		day: date.getDate().toString(),
		month: getMonthLabel(date),
	}
}

export const getProgramWeekDateRangeLabel = (
	weekStartAt: string | null | undefined,
	daysAmount = 7
): string => {
	const startDate = getValidDate(weekStartAt)
	if (!startDate) {
		return ''
	}

	const endDate = addDays(startDate, Math.max(daysAmount - 1, 0))
	const startMonth = getMonthLabel(startDate)
	const endMonth = getMonthLabel(endDate)

	if (startMonth === endMonth) {
		return `${startDate.getDate()}-${endDate.getDate()} ${endMonth}`
	}

	return `${startDate.getDate()} ${startMonth} - ${endDate.getDate()} ${endMonth}`
}

export const getProgramWeekStartDate = (
	programStartedAt: string | null | undefined,
	weekNum: number
): Date | null => {
	const startedAt = getValidDate(programStartedAt)
	if (!startedAt || !Number.isInteger(weekNum) || weekNum < 1) {
		return null
	}

	return addDays(startedAt, (weekNum - 1) * 7)
}
